/**
 * cv-slider — Generic slider with drag, arrows, dots, autoplay.
 * Auto-initializes on all .js-cv-slider elements.
 * Can be re-initialized after DOM changes by calling window.initCvSliders().
 */
(function () {
  'use strict';

  function initSliders() {
    document.querySelectorAll('.js-cv-slider:not(.is-initialized)').forEach(function (container) {
      container.classList.add('is-initialized');

      var track      = container.querySelector('.js-slider-track');
      var nextBtn    = container.querySelector('.js-slider-next');
      var prevBtn    = container.querySelector('.js-slider-prev');
      var dotsEl     = container.querySelector('.js-slider-dots');
      var autoplayMs = parseInt(container.dataset.autoplay, 10) || 0;
      var loopEnabled = container.dataset.loop === 'true';

      // Collect real slides before cloning
      var realSlides = Array.from(track.children);
      var totalReal  = realSlides.length;

      if (!totalReal) return;

      // Loop: clone last slide before first, first slide after last
      var loopOffset = 0;
      if (loopEnabled && totalReal > 1) {
        var cloneLast = realSlides[totalReal - 1].cloneNode(true);
        var cloneFirst = realSlides[0].cloneNode(true);
        cloneLast.classList.add('cv-slider__clone');
        cloneLast.setAttribute('aria-hidden', 'true');
        cloneFirst.classList.add('cv-slider__clone');
        cloneFirst.setAttribute('aria-hidden', 'true');
        track.insertBefore(cloneLast, track.firstChild);
        track.appendChild(cloneFirst);
        loopOffset = 1; // real slide 0 is now at position 1
      }

      var slides = track.children;

      var currentIndex     = 0;
      var currentTranslate = 0;
      var isDragging       = false;
      var dragStartX       = 0;
      var dragStartY       = 0;
      var dragDelta        = 0;
      var autoplayTimer    = null;
      var touchLocked      = false;
      var isHorizontal     = false;
      var isTransitioning  = false;

      // Dots — only for real slides
      if (dotsEl) {
        for (var i = 0; i < totalReal; i++) {
          var dot = document.createElement('button');
          dot.className = 'cv-slider__dot';
          dot.type = 'button';
          dot.setAttribute('aria-label', (document.documentElement.lang.indexOf('fr') === 0 ? 'Diapositive ' : 'Slide ') + (i + 1));
          (function (idx) {
            dot.addEventListener('click', function () { goTo(idx); });
          })(i);
          dotsEl.appendChild(dot);
        }
      }

      function getSlidesPerView() {
        var val = parseFloat(getComputedStyle(container).getPropertyValue('--slides-per-view'));
        return isNaN(val) ? 1 : Math.max(1, val);
      }

      function getMaxIndex() {
        return Math.max(0, totalReal - Math.floor(getSlidesPerView()));
      }

      function getMoveDistance() {
        var ref = slides[loopOffset]; // first real slide
        if (!ref) return 0;
        var gap = parseFloat(getComputedStyle(track).gap) || 0;
        return ref.offsetWidth + gap;
      }

      function getTranslateForIndex(idx) {
        return (idx + loopOffset) * -getMoveDistance();
      }

      var prevIndex = 0;

      function goTo(index, skipEvent) {
        if (isTransitioning) return;
        prevIndex = currentIndex;

        if (loopEnabled && totalReal > 1) {
          // Allow -1 and totalReal to hit clones
          currentIndex = index;
        } else {
          currentIndex = Math.max(0, Math.min(index, getMaxIndex()));
        }

        currentTranslate = getTranslateForIndex(currentIndex);
        track.style.transition = 'transform 0.4s ease-out';
        track.style.transform  = 'translateX(' + currentTranslate + 'px)';

        updateControls();

        if (!skipEvent && currentIndex !== prevIndex) {
          // Normalize index for event
          var eventIndex = currentIndex;
          if (loopEnabled) {
            eventIndex = ((currentIndex % totalReal) + totalReal) % totalReal;
          }
          var eventPrev = ((prevIndex % totalReal) + totalReal) % totalReal;
          container.dispatchEvent(new CustomEvent('slide-change', {
            detail: { index: eventIndex, previousIndex: eventPrev }
          }));
        }

        // After transition, silently jump if we landed on a clone
        if (loopEnabled && totalReal > 1 && (currentIndex < 0 || currentIndex >= totalReal)) {
          isTransitioning = true;
          var jumpTo = currentIndex < 0 ? totalReal - 1 : 0;
          setTimeout(function () {
            track.style.transition = 'none';
            currentIndex = jumpTo;
            currentTranslate = getTranslateForIndex(currentIndex);
            track.style.transform = 'translateX(' + currentTranslate + 'px)';
            isTransitioning = false;
          }, 420);
        }

        resetAutoplay();
      }

      function updateControls() {
        var normalizedIndex = loopEnabled
          ? ((currentIndex % totalReal) + totalReal) % totalReal
          : currentIndex;

        if (prevBtn) prevBtn.disabled = !loopEnabled && currentIndex <= 0;
        if (nextBtn) nextBtn.disabled = !loopEnabled && currentIndex >= getMaxIndex();

        if (dotsEl) {
          Array.from(dotsEl.children).forEach(function (dot, i) {
            dot.classList.toggle('is-active', i === normalizedIndex);
            dot.setAttribute('aria-current', String(i === normalizedIndex));
          });
        }
      }

      // Drag
      function getClientX(e) {
        return e.touches ? e.touches[0].clientX : e.clientX;
      }
      function getClientY(e) {
        return e.touches ? e.touches[0].clientY : e.clientY;
      }

      function onDragStart(e) {
        if (isTransitioning) return;
        isDragging = true;
        dragStartX = getClientX(e);
        dragStartY = getClientY(e);
        dragDelta  = 0;
        touchLocked  = false;
        isHorizontal = false;
        clearInterval(autoplayTimer);
      }

      function onDrag(e) {
        if (!isDragging) return;
        var dx = getClientX(e) - dragStartX;
        var dy = getClientY(e) - dragStartY;

        if (!touchLocked && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
          touchLocked = true;
          isHorizontal = Math.abs(dx) > Math.abs(dy);
          if (isHorizontal) track.style.transition = 'none';
        }

        if (!touchLocked || !isHorizontal) return;

        if (e.cancelable) e.preventDefault();
        dragDelta = dx;
        track.style.transform = 'translateX(' + (currentTranslate + dragDelta) + 'px)';
      }

      function onDragEnd() {
        if (!isDragging) return;
        isDragging = false;
        if (!isHorizontal) return;
        var newIndex = currentIndex;
        if (dragDelta < -50 && (loopEnabled || currentIndex < getMaxIndex())) newIndex++;
        else if (dragDelta > 50 && (loopEnabled || currentIndex > 0)) newIndex--;
        goTo(newIndex);
      }

      track.addEventListener('click', function (e) {
        if (Math.abs(dragDelta) > 5) { e.preventDefault(); e.stopPropagation(); }
      }, true);

      track.addEventListener('dragstart', function (e) { e.preventDefault(); });
      track.addEventListener('mousedown',  onDragStart);
      track.addEventListener('touchstart', onDragStart, { passive: true });
      track.addEventListener('mousemove',  onDrag);
      track.addEventListener('touchmove',  onDrag, { passive: false });
      track.addEventListener('touchend',   onDragEnd);
      document.addEventListener('mouseup',  onDragEnd);

      if (nextBtn) nextBtn.addEventListener('click', function () { goTo(currentIndex + 1); });
      if (prevBtn) prevBtn.addEventListener('click', function () { goTo(currentIndex - 1); });

      container.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); goTo(currentIndex + 1); }
        if (e.key === 'ArrowLeft')  { e.preventDefault(); goTo(currentIndex - 1); }
      });

      // Autoplay
      function startAutoplay() {
        if (!autoplayMs) return;
        autoplayTimer = setInterval(function () {
          goTo(currentIndex + 1);
        }, autoplayMs);
      }

      function resetAutoplay() {
        clearInterval(autoplayTimer);
        startAutoplay();
      }

      if (autoplayMs) {
        container.addEventListener('mouseenter', function () { clearInterval(autoplayTimer); });
        container.addEventListener('mouseleave', startAutoplay);
        container.addEventListener('focusin',    function () { clearInterval(autoplayTimer); });
        container.addEventListener('focusout',   startAutoplay);
      }

      // Resize
      var resizeTimer;
      window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
          track.style.transition = 'none';
          currentTranslate = getTranslateForIndex(currentIndex);
          track.style.transform = 'translateX(' + currentTranslate + 'px)';
        }, 150);
      });

      // Expose goTo for external control
      container.goTo = goTo;
      container.getCurrentIndex = function () { return currentIndex; };

      // Initial position (skip to real slide 0, accounting for clone offset)
      track.style.transition = 'none';
      currentTranslate = getTranslateForIndex(0);
      track.style.transform = 'translateX(' + currentTranslate + 'px)';
      updateControls();
      startAutoplay();
    });
  }

  // Expose for re-initialization (e.g. after Section Rendering API morph)
  window.initCvSliders = initSliders;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSliders);
  } else {
    initSliders();
  }
})();
